import { useCallback, useEffect, useRef, useState } from 'react';
import * as handOrderGrantAnimationDetector from './handOrderGrantAnimationDetector';
import { buildRecoverHealLogDedupeKey } from './useViralEchoHealFx';
import { GENERAL_HEAL_FLASH_DURATION_MS } from '../config/generalHealFlashEffect';

const { resolveProviderUnitSlot } = handOrderGrantAnimationDetector;

const processedKeysByGame = new Map();
const hydratedGames = new Set();

/**
 * heal.png on the source unit when recover-healed-general is logged (Recover, unit 26 barrier heal, etc.).
 */
export function useUnitHealFlashFx(gameState, viewerUserId) {
  const [activeFlashes, setActiveFlashes] = useState([]);
  const gameIdRef = useRef(null);
  const prevActionLogLenRef = useRef(0);

  const dismissFlash = useCallback((key) => {
    setActiveFlashes((prev) => prev.filter((flash) => flash.key !== key));
  }, []);

  useEffect(() => {
    if (!gameState?.state || viewerUserId == null) {
      return undefined;
    }

    const gid = gameState.gameId != null ? String(gameState.gameId) : '_';

    if (gameIdRef.current !== gid) {
      gameIdRef.current = gid;
      setActiveFlashes([]);
      prevActionLogLenRef.current = 0;
    }

    if (!processedKeysByGame.has(gid)) {
      processedKeysByGame.set(gid, new Set());
    }
    const processed = processedKeysByGame.get(gid);

    const actionLog = Array.isArray(gameState.actionLog) ? gameState.actionLog : [];

    if (!hydratedGames.has(gid)) {
      hydratedGames.add(gid);
      actionLog.forEach((entry, index) => {
        if (entry?.type === 'recover-healed-general') {
          processed.add(buildRecoverHealLogDedupeKey(entry, index));
        }
      });
      prevActionLogLenRef.current = actionLog.length;
      return undefined;
    }

    const prevLen = prevActionLogLenRef.current;
    if (actionLog.length > prevLen) {
      for (let i = prevLen; i < actionLog.length; i += 1) {
        const entry = actionLog[i];
        if (!entry || entry.type !== 'recover-healed-general') {
          continue;
        }
        const dedupeKey = buildRecoverHealLogDedupeKey(entry, i);
        if (processed.has(dedupeKey)) {
          continue;
        }
        processed.add(dedupeKey);

        const sourceInstanceId = entry.source?.instanceId;
        if (!sourceInstanceId) {
          throw new Error(
            `useUnitHealFlashFx: recover-healed-general missing source.instanceId (log index ${i})`
          );
        }

        const ownerPlayerId = entry.playerId ?? entry.generalPlayerId;
        if (!ownerPlayerId) {
          throw new Error(
            `useUnitHealFlashFx: recover-healed-general missing playerId (log index ${i})`
          );
        }

        const slot = resolveProviderUnitSlot(
          gameState,
          { instanceId: sourceInstanceId },
          viewerUserId
        );
        if (!slot) {
          continue;
        }

        const animKey = `unit-heal-${sourceInstanceId}-${Date.now()}`;
        setActiveFlashes((prev) => [
          ...prev,
          {
            key: animKey,
            viewerZone: slot.zone,
            slotIndex: slot.index,
            animationKey: animKey,
          },
        ]);
        window.setTimeout(() => {
          setActiveFlashes((prev) => prev.filter((flash) => flash.key !== animKey));
        }, GENERAL_HEAL_FLASH_DURATION_MS + 80);
      }
    }
    prevActionLogLenRef.current = actionLog.length;

    const setForGame = processedKeysByGame.get(gid);
    if (setForGame && setForGame.size > 120) {
      const keys = Array.from(setForGame);
      keys.slice(0, keys.length - 80).forEach((k) => setForGame.delete(k));
    }

    return undefined;
  }, [gameState, gameState?.stateVersion, gameState?.actionLog?.length, viewerUserId]);

  return { activeUnitHealFlashes: activeFlashes, dismissUnitHealFlash: dismissFlash };
}

export default useUnitHealFlashFx;
